import {
  describeSystemLogTarget,
  getSafeBrowserLocation,
  getSystemLogBuildMetadata,
  inferSystemLogCategory,
  inferSystemLogEventKind,
  normalizeSystemLogSeverity,
  sanitizeSystemLogText,
} from './systemLogContract';
import {createSystemLogBatcher} from './systemLogBatcher';

const LOGGING_FUNCTION_URL = 'https://logerror-flrdxmgkwa-uc.a.run.app';
const FALLBACK_SYSTEM_VERSION = '0.1.0';
const DEFAULT_PORTAL = 'portal-servidor';

const sessionId = `${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;

let lastAction = 'Navegacao inicial';
let currentPortal = DEFAULT_PORTAL;
let userInfoResolver = null;
let consoleCaptureInstalled = false;
let originalConsoleError = null;

const sendLogPayload = (payload) => {
  try {
    return fetch(LOGGING_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit'
    }).then((response) => response?.ok === true).catch(() => false);
  } catch (errorToIgnore) {
    return Promise.resolve(false);
  }
};

const systemLogBatcher = createSystemLogBatcher({send: sendLogPayload, waitMs: 30000, maxBatchSize: 100});

if (typeof window !== 'undefined' && !window.__sisfolServidorLogBatchFlushRegistered) {
  window.__sisfolServidorLogBatchFlushRegistered = true;
  const flushPendingSystemLogs = () => { void systemLogBatcher.flushAll(); };
  window.addEventListener('pagehide', flushPendingSystemLogs);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPendingSystemLogs();
  });
}

const safeString = (value, maxLength = 800) => {
  if (value === null || value === undefined) return '';

  let text = '';

  if (value instanceof Error) {
    text = value.message || value.name || 'Erro sem mensagem';
  } else if (typeof value === 'object') {
    const summary = {};
    if (value.code !== undefined) summary.code = value.code;
    if (value.message !== undefined) summary.message = value.message;
    const keys = Object.keys(value).filter((key) => !/password|senha|token|secret|cpf|email|telefone|endereco|nome/i.test(key)).slice(0, 8);
    text = Object.keys(summary).length > 0
      ? JSON.stringify(summary)
      : `[object keys=${keys.join(',') || 'none'}]`;
  } else {
    text = String(value);
  }

  return sanitizeSystemLogText(text, maxLength);
};

const normalizeUserInfo = (userInfo) => {
  if (!userInfo) return null;

  return {
    role: userInfo.role || userInfo.perfil || null,
  };
};

const normalizeExtra = (extra = {}) => ({
  source: safeString(extra.source, 120) || null,
  module: safeString(extra.module, 160) || null,
  listenerName: safeString(extra.listenerName, 200) || null,
  collectionPath: safeString(extra.collectionPath, 600) || null,
  category: safeString(extra.category, 120) || null,
  severity: safeString(extra.severity, 40) || null,
  operation: safeString(extra.operation || extra.operacao, 200) || null,
  functionName: safeString(extra.functionName || extra.function, 200) || null,
  code: safeString(extra.code || extra.errorCode, 200) || null,
  region: safeString(extra.region, 80) || null,
  requestId: safeString(extra.requestId, 200) || null,
  correlationId: safeString(extra.correlationId, 200) || null,
  context: safeString(extra.context, 1200) || null,
  callerStack: safeString(extra.callerStack, 3000) || null,
});

const getDefaultSource = (type = '') => {
  if (type === 'REACT_ERROR') return 'react-error-boundary';
  if (type === 'PROMISE_ERROR') return 'unhandledrejection';
  if (type === 'JS_ERROR') return 'window-error';
  return 'application';
};

const resolveUserInfo = (providedUserInfo) => {
  if (providedUserInfo) return providedUserInfo;

  if (typeof userInfoResolver !== 'function') return null;

  try {
    return userInfoResolver();
  } catch (error) {
    return null;
  }
};

const getDateParts = () => {
  const now = new Date();
  const localDate = new Date(now.getTime() - (now.getTimezoneOffset() * 60000));

  return {
    now,
    date: localDate.toISOString().split('T')[0],
    time: now.toTimeString().split(' ')[0]
  };
};

if (typeof window !== 'undefined' && !window.__sisfolServidorLastActionListenerRegistered) {
  window.__sisfolServidorLastActionListenerRegistered = true;

  window.addEventListener('click', (event) => {
    lastAction = describeSystemLogTarget(event.target, 'click');
  }, { capture: true });

  window.addEventListener('submit', (event) => {
    lastAction = describeSystemLogTarget(event.target, 'submit');
  }, { capture: true });
}

export const getLastUserAction = () => lastAction;

export const getSystemLogSessionId = () => sessionId;
export const flushSystemLogBatch = () => systemLogBatcher.flushAll();

export const setSystemLogPortal = (portalName = DEFAULT_PORTAL) => {
  currentPortal = portalName || DEFAULT_PORTAL;
};

export const setSystemLogUserResolver = (resolver) => {
  userInfoResolver = typeof resolver === 'function' ? resolver : null;
};

export const logErrorToService = (
  level,
  portalName = DEFAULT_PORTAL,
  error,
  userInfo = null,
  type = 'JS_ERROR',
  reactStack = null,
  extra = {}
) => {
  if (!LOGGING_FUNCTION_URL || LOGGING_FUNCTION_URL.includes('SEU-HASH')) {
    return Promise.resolve(false);
  }

  const normalizedLevel = ['error', 'warn', 'info'].includes(level) ? level : 'error';
  const normalizedPortal = portalName || currentPortal || DEFAULT_PORTAL;

  const message = error instanceof Error
    ? safeString(error.message || error.name || 'Erro sem mensagem', 1200)
    : safeString(error, 1200);

  if (!message) {
    return Promise.resolve(false);
  }

  const { now, date, time } = getDateParts();
  const errorObject = error instanceof Error ? error : null;
  const resolvedUserInfo = resolveUserInfo(userInfo);
  const normalizedExtra = normalizeExtra(extra);
  const code = normalizedExtra.code || safeString(errorObject?.code || error?.code, 200) || null;
  const eventKind = inferSystemLogEventKind({
    eventKind: extra.eventKind,
    type,
    code,
    message,
    level: normalizedLevel,
  });
  const category = inferSystemLogCategory({
    category: normalizedExtra.category,
    eventKind,
    type,
    code,
    message,
  });
  const source = normalizedExtra.source || getDefaultSource(type);
  const severity = normalizeSystemLogSeverity(normalizedExtra.severity, eventKind, normalizedLevel);
  const location = getSafeBrowserLocation();
  const normalizedUserInfo = normalizeUserInfo(resolvedUserInfo);

  const logPayload = {
    ...getSystemLogBuildMetadata(normalizedPortal, FALLBACK_SYSTEM_VERSION),
    level: normalizedLevel,
    portal: normalizedPortal,
    type: type || 'JS_ERROR',
    eventKind,
    message,
    stack: errorObject ? safeString(errorObject.stack, 8000) : null,
    reactStack: reactStack ? safeString(reactStack, 8000) : null,
    timestamp: now.toISOString(),
    date,
    time,
    url: location.url,
    route: location.route,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
    sessionId,
    lastAction,
    userInfo: normalizedUserInfo,
    userRole: normalizedUserInfo?.role || null,
    ...normalizedExtra,
    operation: normalizedExtra.operation || (lastAction.startsWith('action:') ? lastAction.slice(7) : null),
    source,
    category,
    code,
    severity,
  };

  return systemLogBatcher.enqueue(logPayload);
};

export const logWarningToService = (message, userInfo = null, type = 'WARN', extra = {}) => {
  return logErrorToService('warn', currentPortal || DEFAULT_PORTAL, message, userInfo, type, null, extra);
};

export const logInfoToService = (message, userInfo = null, type = 'INFO', extra = {}) => {
  return logErrorToService('info', currentPortal || DEFAULT_PORTAL, message, userInfo, type, null, extra);
};

const getErrorLogContext = (error) => {
  if (!error || typeof error !== 'object') return {};
  const context = error.sisdpLogContext;
  return context && typeof context === 'object' ? context : {};
};

/** Mantém login, validação e strings arbitrárias do console fora dos logs. */
export const handleConsoleCapture = (args = []) => {
  const error = Array.isArray(args) ? args.find((value) => value instanceof Error) : null;
  if (!error) return false;
  const eventKind = inferSystemLogEventKind({
    type: 'CONSOLE_ERROR',
    code: error.code,
    message: error.message,
    level: 'error',
  });
  if (eventKind !== 'technical_error') return false;
  void logErrorToService('error', DEFAULT_PORTAL, error, null, 'CONSOLE_ERROR', null, {
    ...getErrorLogContext(error),
    source: 'console-capture',
    context: `consoleArgs:${args.length}`,
  });
  return true;
};

export const installConsoleLogCapture = () => {
  if (typeof window === 'undefined' || consoleCaptureInstalled) return () => {};
  consoleCaptureInstalled = true;
  originalConsoleError = console.error;
  console.error = (...args) => {
    originalConsoleError.apply(console, args);
    try {
      handleConsoleCapture(args);
    } catch (_ignored) {
      // Registrar um erro nunca deve interromper o fluxo original.
    }
  };
  return () => {
    if (!consoleCaptureInstalled) return;
    console.error = originalConsoleError;
    consoleCaptureInstalled = false;
  };
};
